<template>
  <div
    class="whatsapp-support"
    :data-panel-release="$panelRelease"
  >
    <v-alert
      v-if="error"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <v-row>
      <v-col
        cols="12"
        lg="4"
      >
        <v-card class="support-panel">
          <v-card-title class="support-panel__title">
            <div>
              <div class="text-h6 font-weight-bold">
                Gelen Kutusu
              </div>
              <div class="text-caption text--secondary">
                {{ filteredConversations.length }} / {{ conversations.length }} konuşma · Son 200 kayıt
              </div>
            </div>
            <div class="support-panel__actions">
              <v-btn
                small
                outlined
                color="primary"
                :disabled="reviewDirty || reviewBusy"
                @click="showNewConversationDialog"
              >
                <v-icon
                  left
                  small
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                Yeni mesaj
              </v-btn>
              <v-btn
                icon
                :loading="loadingConversations"
                aria-label="Gelen kutusunu yenile"
                @click="refreshInbox"
              >
                <v-icon>{{ icons.mdiRefresh }}</v-icon>
              </v-btn>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <div class="pa-4">
            <v-text-field
              v-model="inboxSearch"
              label="Kişi, telefon veya mesaj ara"
              outlined
              dense
              clearable
              hide-details
            />
            <v-row
              dense
              class="mt-2"
            >
              <v-col cols="6">
                <v-select
                  v-model="inboxState"
                  label="Durum"
                  :items="inboxStates"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="inboxAge"
                  label="Son mesaj"
                  :items="inboxAges"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>
            <v-text-field
              v-model="inboxAssignee"
              label="Sorumlu ara"
              outlined
              dense
              clearable
              hide-details
              class="mt-2"
              :disabled="Boolean(reviewLoadError)"
            />
            <v-alert
              v-if="reviewLoadError"
              type="warning"
              dense
              text
              class="mt-2"
            >
              {{ reviewLoadError }}
            </v-alert>
          </div>

          <v-list
            two-line
            class="support-conversations"
          >
            <v-list-item
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              :class="{ 'support-conversation--active': selectedConversation && selectedConversation.id === conversation.id }"
              :disabled="reviewDirty || reviewBusy || sending || sendingTemplate"
              @click="selectConversation(conversation)"
            >
              <v-list-item-avatar color="success">
                <span class="white--text text-caption font-weight-bold">
                  {{ initials(conversation) }}
                </span>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-semibold">
                  {{ contactTitle(conversation) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ conversation.lastMessagePreview || '-' }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="reviews[conversation.id]">
                  {{ reviews[conversation.id].assignee || 'Atanmadı' }} · {{ priorityLabel(reviews[conversation.id].priority) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="support-conversation__meta">
                <span class="text-caption text--secondary">{{ formatTime(conversation.lastMessageAt) }}</span>
                <v-chip
                  v-if="conversation.unreadCount"
                  x-small
                  color="primary"
                  class="mt-2"
                >
                  {{ conversation.unreadCount }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>

            <v-list-item v-if="!loadingConversations && !error && filteredConversations.length === 0">
              <v-list-item-content>
                <v-list-item-title class="text--secondary">
                  Filtrelere uygun konuşma yok
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="8"
      >
        <v-card class="support-panel support-thread">
          <template v-if="selectedConversation">
            <v-card-title class="support-panel__title">
              <div>
                <div class="text-h6 font-weight-bold">
                  {{ contactTitle(selectedConversation) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ selectedConversation.contactWaId }}
                </div>
              </div>
              <div class="support-thread__actions">
                <v-tooltip
                  v-if="selectedConversation.phoneVerified === false"
                  bottom
                  max-width="380"
                >
                  <template #activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      small
                      outlined
                      color="warning"
                      v-on="on"
                    >
                      Telefon doğrulanmadı
                    </v-chip>
                  </template>
                  <span>Bu telefon kullanıcı tarafından doğrulanmamış. Numarayı kontrol ederek onaylı WhatsApp template'i gönderebilirsiniz.</span>
                </v-tooltip>

                <v-tooltip
                  v-if="!canReply"
                  bottom
                  max-width="380"
                >
                  <template #activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      small
                      outlined
                      color="warning"
                      v-on="on"
                    >
                      24 saat kapalı
                    </v-chip>
                  </template>
                  <span>{{ replyBlockedReason }}</span>
                </v-tooltip>

                <v-menu
                  v-model="templateMenu"
                  :close-on-content-click="false"
                  offset-y
                  left
                  min-width="360"
                  max-width="420"
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      small
                      outlined
                      color="success"
                      v-on="on"
                    >
                      Hazır mesaj
                      <v-icon
                        right
                        small
                      >
                        {{ icons.mdiChevronDown }}
                      </v-icon>
                    </v-btn>
                  </template>

                  <v-card class="support-template-menu pa-4">
                    <div class="d-flex align-center justify-space-between">
                      <div class="text-subtitle-2 font-weight-bold">
                        WhatsApp template'i
                      </div>
                      <v-chip
                        v-if="selectedSupportTemplate"
                        x-small
                        :color="templateStatusColor"
                        outlined
                      >
                        {{ templateStatusLabel }}
                      </v-chip>
                    </div>

                    <v-alert
                      v-if="templateError"
                      dense
                      outlined
                      type="error"
                      class="mt-3 mb-0"
                    >
                      {{ templateError }}
                    </v-alert>

                    <template v-else>
                      <v-select
                        v-model="selectedTemplateName"
                        :items="supportTemplates"
                        item-text="displayName"
                        item-value="name"
                        :loading="loadingTemplates"
                        :disabled="loadingTemplates || sendingTemplate || !supportTemplates.length"
                        dense
                        outlined
                        hide-details
                        class="mt-3"
                        placeholder="Template seç"
                      ></v-select>
                      <div
                        v-if="selectedSupportTemplate"
                        class="support-template__preview"
                      >
                        {{ selectedSupportTemplate.body }}
                        <div
                          v-if="selectedSupportTemplate.footer"
                          class="text-caption text--secondary mt-1"
                        >
                          {{ selectedSupportTemplate.footer }}
                        </div>
                      </div>
                      <v-btn
                        color="success"
                        small
                        :loading="sendingTemplate"
                        :disabled="!canSendTemplate"
                        @click="sendSupportTemplate"
                      >
                        <v-icon
                          left
                          small
                        >
                          {{ icons.mdiWhatsapp }}
                        </v-icon>
                        Template gönder
                      </v-btn>
                      <div
                        v-if="templateSendBlockedReason"
                        class="text-caption text--secondary mt-2"
                      >
                        {{ templateSendBlockedReason }}
                      </div>
                    </template>
                  </v-card>
                </v-menu>

                <v-chip
                  small
                  outlined
                  color="success"
                >
                  {{ selectedConversation.status === 'CLOSED' ? 'Kapalı' : 'Açık' }}
                </v-chip>
                <v-btn
                  v-if="selectedConversation.userId"
                  small
                  text
                  color="primary"
                  :to="{ name: 'userList', query: { userId: String(selectedConversation.userId) } }"
                  :disabled="reviewDirty || reviewBusy"
                >
                  Kullanıcı kaydı
                </v-btn>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <details class="pa-4 support-review-details">
              <summary>Sorumlu, öncelik ve iç not <span v-if="reviewDirty"> · Kaydedilmemiş değişiklik</span></summary>
              <support-review-editor
                :key="selectedConversation.id"
                channel="WHATSAPP"
                :record-id="selectedConversation.id"
                class="mt-3"
                @dirty="reviewDirty = $event"
                @busy="reviewBusy = $event"
                @saved="reviewSaved"
              />
            </details>

            <div
              ref="messagePane"
              class="support-messages"
            >
              <div
                v-if="hasMoreMessages"
                class="support-history-loader"
              >
                <v-btn
                  small
                  outlined
                  color="primary"
                  :loading="loadingOlderMessages"
                  :disabled="loadingOlderMessages"
                  @click="loadOlderMessages"
                >
                  Daha eski mesajları yükle
                </v-btn>
              </div>
              <div
                v-for="message in visibleMessages"
                :key="message.id"
                class="support-message"
                :class="{
                  'support-message--system': isSystemMessage(message),
                  'support-message--outbound': !isSystemMessage(message) && message.direction === 'OUTBOUND',
                  'support-message--inbound': !isSystemMessage(message) && message.direction !== 'OUTBOUND',
                }"
              >
                <div class="support-message__content">
                  <div class="support-message__bubble">
                    <div
                      v-if="message.replyToMetaMessageId"
                      class="support-message__quote"
                    >
                      <strong>{{ replySenderLabel(replyTargetFor(message)) }}</strong>
                      <span>{{ replyPreview(replyTargetFor(message)) }}</span>
                    </div>
                    <div
                      v-if="isKnownMediaMessage(message)"
                      class="support-message__media"
                    >
                      <div
                        v-if="!message.mediaAvailable"
                        class="support-message__media-state support-message__media-state--unavailable"
                      >
                        {{ mediaUnavailableLabel(message) }}
                      </div>
                      <div
                        v-else-if="mediaLoading[message.id]"
                        class="support-message__media-state"
                      >
                        <v-progress-circular
                          indeterminate
                          size="22"
                          width="2"
                          color="primary"
                        ></v-progress-circular>
                        <span>Medya yükleniyor</span>
                      </div>
                      <template v-else-if="mediaObjectUrls[message.id]">
                        <img
                          v-if="isImageMedia(message)"
                          :src="mediaObjectUrls[message.id]"
                          :alt="mediaCaption(message) || 'WhatsApp görseli'"
                          class="support-message__image"
                          @click="openMedia(message)"
                        >
                        <video
                          v-else-if="isVideoMedia(message)"
                          :src="mediaObjectUrls[message.id]"
                          class="support-message__video"
                          controls
                          preload="metadata"
                        ></video>
                        <audio
                          v-else-if="isAudioMedia(message)"
                          :src="mediaObjectUrls[message.id]"
                          class="support-message__audio"
                          controls
                          preload="metadata"
                        ></audio>
                        <a
                          v-else
                          :href="mediaObjectUrls[message.id]"
                          :download="message.mediaFileName || 'whatsapp-dosya'"
                          class="support-message__document"
                        >
                          <v-icon
                            small
                            left
                          >{{ icons.mdiFileDownloadOutline }}</v-icon>
                          {{ message.mediaFileName || 'Dosyayı indir' }}
                        </a>
                      </template>
                      <button
                        v-else
                        type="button"
                        class="support-message__media-error"
                        @click="loadMessageMedia(message)"
                      >
                        {{ mediaErrors[message.id] ? 'Medya yüklenemedi. Tekrar dene' : mediaLoadLabel(message) }}
                      </button>
                      <div
                        v-if="mediaCaption(message)"
                        class="support-message__body support-message__caption"
                      >
                        {{ mediaCaption(message) }}
                      </div>
                    </div>
                    <div
                      v-else
                      class="support-message__body"
                    >
                      <template v-if="isReactionMessage(message)">
                        {{ message.reactionEmoji || '↪' }} Reaksiyon
                        <span class="support-message__reaction-note">(hedef mesaj yüklenemedi)</span>
                      </template>
                      <template v-else>
                        {{ message.body }}
                      </template>
                    </div>
                    <div class="support-message__meta">
                      <span>{{ formatTime(message.messageTimestamp) }}</span>
                      <span v-if="message.direction === 'OUTBOUND' || isSystemMessage(message)"> · {{ message.messageStatus || 'sent' }}</span>
                    </div>
                  </div>

                  <div
                    v-if="messageReactions(message).length"
                    class="support-message__reactions"
                  >
                    <button
                      v-for="reaction in messageReactions(message)"
                      :key="reaction.id"
                      type="button"
                      class="support-message__reaction"
                      :class="{ 'support-message__reaction--ours': reaction.direction === 'OUTBOUND' }"
                      :title="reaction.direction === 'OUTBOUND' ? 'Panel reaksiyonu' : 'Kullanıcı reaksiyonu'"
                      @click="reaction.direction === 'OUTBOUND' ? sendReaction(message, reaction.reactionEmoji) : null"
                    >
                      {{ reaction.reactionEmoji }}
                    </button>
                  </div>

                  <div
                    v-if="canReplyToMessage(message) || canReactTo(message)"
                    class="support-message__actions"
                  >
                    <v-btn
                      v-if="canReplyToMessage(message)"
                      icon
                      x-small
                      class="support-message__action"
                      title="Mesajı yanıtla"
                      @click="selectReplyTarget(message)"
                    >
                      <v-icon small>
                        {{ icons.mdiReply }}
                      </v-icon>
                    </v-btn>
                    <v-menu
                      v-if="canReactTo(message)"
                      offset-y
                      :close-on-content-click="true"
                    >
                      <template #activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          icon
                          x-small
                          class="support-message__action"
                          :loading="isSendingReaction(message)"
                          title="Mesaja reaksiyon ver"
                          v-on="on"
                        >
                          <v-icon small>
                            {{ icons.mdiEmoticonOutline }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <div class="support-emoji-picker support-emoji-picker--reaction">
                        <button
                          v-for="emoji in reactionEmojis"
                          :key="emoji"
                          type="button"
                          class="support-emoji-picker__item"
                          @click="sendReaction(message, emoji)"
                        >
                          {{ emoji }}
                        </button>
                      </div>
                    </v-menu>
                  </div>
                </div>
              </div>
            </div>

            <v-divider></v-divider>

            <div class="support-reply">
              <div class="support-reply__body">
                <v-alert
                  v-if="hiddenOutboundMessageCount"
                  dense
                  outlined
                  type="warning"
                  class="mb-3"
                >
                  Meta tarafından başarısız olarak bildirilen {{ hiddenOutboundMessageCount }} WhatsApp mesajı chat geçmişinden kaldırıldı.
                </v-alert>
                <div
                  v-if="replyTarget"
                  class="support-reply-target"
                >
                  <div class="support-reply-target__content">
                    <strong>{{ replySenderLabel(replyTarget) }}</strong>
                    <span>{{ replyPreview(replyTarget) }}</span>
                  </div>
                  <v-btn
                    icon
                    x-small
                    title="Yanıt seçimini kaldır"
                    @click="clearReplyTarget"
                  >
                    <v-icon small>
                      {{ icons.mdiClose }}
                    </v-icon>
                  </v-btn>
                </div>
                <div class="support-reply__editor">
                  <v-textarea
                    ref="replyInput"
                    v-model="reply"
                    rows="3"
                    auto-grow
                    outlined
                    dense
                    hide-details
                    :placeholder="canReply ? 'Cevap yaz' : 'Serbest mesaj için 24 saatlik pencere kapalı'"
                    :disabled="sending || !canReply"
                    @keydown.enter.exact.prevent="sendReply"
                  ></v-textarea>
                  <v-menu
                    v-model="emojiMenu"
                    top
                    left
                    offset-y
                    :close-on-content-click="false"
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        icon
                        small
                        class="support-reply__emoji-button"
                        :disabled="sending || !canReply"
                        title="Emoji ekle"
                        v-on="on"
                      >
                        <v-icon>{{ icons.mdiEmoticonOutline }}</v-icon>
                      </v-btn>
                    </template>
                    <div class="support-emoji-picker support-emoji-picker--message">
                      <div class="support-emoji-picker__categories">
                        <button
                          v-for="category in emojiCategories"
                          :key="category.id"
                          type="button"
                          class="support-emoji-picker__category"
                          :class="{ 'support-emoji-picker__category--active': activeEmojiCategory === category.id }"
                          :title="category.label"
                          @click="activeEmojiCategory = category.id"
                        >
                          {{ category.icon }}
                        </button>
                      </div>
                      <div class="support-emoji-picker__grid">
                        <button
                          v-for="emoji in activeMessageEmojis"
                          :key="emoji"
                          type="button"
                          class="support-emoji-picker__item"
                          @click="insertEmoji(emoji)"
                        >
                          {{ emoji }}
                        </button>
                      </div>
                    </div>
                  </v-menu>
                </div>
              </div>
              <v-btn
                color="primary"
                class="ml-3"
                :loading="sending"
                :disabled="!reply.trim() || !canReply"
                @click="sendReply"
              >
                <v-icon left>
                  {{ icons.mdiSend }}
                </v-icon>
                Gönder
              </v-btn>
            </div>
          </template>

          <div
            v-else
            class="support-empty"
          >
            <v-icon
              size="40"
              color="success"
            >
              {{ icons.mdiWhatsapp }}
            </v-icon>
            <div class="mt-3 text-subtitle-1 font-weight-semibold">
              Konuşma seç
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="newConversationDialog"
      max-width="620"
      @click:outside="closeNewConversationDialog"
    >
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          Kullanıcıya WhatsApp gönder
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 text--secondary mb-4">
            Kullanıcı ID, ad, e-posta veya telefonla arayın. Yeni konuşmada ilk gönderim Meta onaylı hazır mesajla yapılır.
          </div>

          <v-alert
            v-if="recipientError"
            dense
            outlined
            type="error"
            class="mb-4"
          >
            {{ recipientError }}
          </v-alert>

          <v-autocomplete
            v-model="selectedRecipientId"
            :search-input.sync="recipientSearch"
            :items="recipientOptions"
            :loading="searchingRecipients"
            item-text="label"
            item-value="userId"
            item-disabled="disabled"
            label="Kullanıcı ara"
            placeholder="ID, ad, e-posta veya telefon"
            clearable
            cache-items
            outlined
            hide-details
            @change="rememberSelectedRecipient"
            @update:search-input="queueRecipientSearch"
          >
            <template #item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.displayName }}</v-list-item-title>
                <v-list-item-subtitle>
                  #{{ item.userId }} · {{ item.email || '-' }} · {{ item.mobile || 'Telefon yok' }}
                </v-list-item-subtitle>
                <v-list-item-subtitle
                  v-if="item.disabled"
                  class="error--text"
                >
                  WhatsApp için geçerli telefon bulunamadı
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-chip
                v-if="item.conversationId"
                x-small
                outlined
                color="success"
              >
                Mevcut konuşma
              </v-chip>
              <v-chip
                v-if="!item.phoneVerified"
                x-small
                outlined
                color="warning"
                class="ml-2"
              >
                Telefon doğrulanmadı
              </v-chip>
            </template>
          </v-autocomplete>

          <div
            v-if="selectedRecipient && !selectedRecipient.phoneVerified"
            class="mt-3 d-flex align-center"
          >
            <v-chip
              small
              outlined
              color="warning"
            >
              Telefon doğrulanmadı
            </v-chip>
            <span class="text-caption text--secondary ml-2">Numarayı kontrol ederek mesaj ekranını açabilirsiniz.</span>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="openingConversation"
            @click="closeNewConversationDialog"
          >
            Vazgeç
          </v-btn>
          <v-btn
            color="primary"
            :loading="openingConversation"
            :disabled="!selectedRecipient || selectedRecipient.disabled"
            @click="openRecipientConversation"
          >
            Mesaj ekranını aç
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiChevronDown,
  mdiClose,
  mdiEmoticonOutline,
  mdiFileDownloadOutline,
  mdiPlus,
  mdiRefresh,
  mdiReply,
  mdiSend,
  mdiWhatsapp,
} from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import emojiCategories from './whatsappEmojiCategories'
import SupportReviewEditor from '@/components/admin/SupportReviewEditor.vue'
import { parseAdminDate } from '@/utils/adminDate'

export default {
  name: 'WhatsAppSupportInbox',
  components: { SupportReviewEditor },
  data() {
    return {
      icons: {
        mdiRefresh,
        mdiReply,
        mdiSend,
        mdiWhatsapp,
        mdiChevronDown,
        mdiClose,
        mdiEmoticonOutline,
        mdiFileDownloadOutline,
        mdiPlus,
      },
      conversations: [],
      inboxSearch: '',
      inboxState: 'ALL',
      inboxAge: 0,
      inboxAssignee: '',
      reviews: {},
      reviewLoadError: '',
      reviewDirty: false,
      reviewBusy: false,
      conversationSequence: 0,
      inboxStates: [{ text: 'Tümü', value: 'ALL' }, { text: 'Okunmamış', value: 'UNREAD' }, { text: 'Açık', value: 'OPEN' }, { text: 'Kapalı', value: 'CLOSED' }],
      inboxAges: [{ text: 'Tümü', value: 0 }, { text: '24 saatten eski', value: 24 }, { text: '3 günden eski', value: 72 }],
      selectedConversation: null,
      messages: [],
      supportTemplates: [],
      selectedTemplateName: '',
      reply: '',
      replyTarget: null,
      emojiMenu: false,
      emojiCategories,
      activeEmojiCategory: emojiCategories[0].id,
      reactionEmojis: ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🎉'],
      sendingReactions: {},
      mediaObjectUrls: {},
      mediaLoading: {},
      mediaErrors: {},
      loadingConversations: false,
      loadingMessages: false,
      loadingOlderMessages: false,
      loadingTemplates: false,
      sending: false,
      sendingTemplate: false,
      templateMenu: false,
      newConversationDialog: false,
      recipientSearch: '',
      recipients: [],
      selectedRecipientId: null,
      selectedRecipientSnapshot: null,
      searchingRecipients: false,
      openingConversation: false,
      recipientError: '',
      recipientLookupTimeout: null,
      error: '',
      templateError: '',
      serverHiddenOutboundMessageCount: 0,
      hasMoreMessages: false,
      nextBeforeMessageId: null,
    }
  },
  computed: {
    filteredConversations() {
      const query = this.inboxSearch.trim().toLocaleLowerCase('tr-TR')
      const assignee = this.inboxAssignee.trim().toLocaleLowerCase('tr-TR')

      return this.conversations.filter(row => {
        const matchesQuery = !query || [row.contactName, row.contactWaId, row.lastMessagePreview].some(value => String(value || '').toLocaleLowerCase('tr-TR').includes(query))
        const matchesState = this.inboxState === 'ALL' || (this.inboxState === 'UNREAD' ? row.unreadCount > 0 : row.status === this.inboxState)
        const date = parseAdminDate(row.lastMessageAt)
        const matchesAge = !this.inboxAge || (date && Date.now() - date.getTime() >= this.inboxAge * 3600000)
        const matchesAssignee = !assignee || (!this.reviewLoadError && String(this.reviews[row.id]?.assignee || '').toLocaleLowerCase('tr-TR').includes(assignee))

        return matchesQuery && matchesState && matchesAge && matchesAssignee
      })
    },
    requestOptions() {
      return {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    },
    apiBase() {
      return `${process.env.VUE_APP_BASE_API_URL}admin/whatsapp-support`
    },
    visibleMessages() {
      return this.messages.filter(
        message => (!this.isReactionMessage(message) || !this.hasReactionTarget(message))
          && !this.shouldHideMessage(message),
      )
    },
    activeMessageEmojis() {
      return this.emojiCategories.find(category => category.id === this.activeEmojiCategory)?.emojis || []
    },
    hiddenOutboundMessageCount() {
      return this.serverHiddenOutboundMessageCount + this.messages.filter(this.shouldHideMessage).length
    },
    selectedSupportTemplate() {
      return this.supportTemplates.find(template => template.name === this.selectedTemplateName) || null
    },
    recipientOptions() {
      return this.recipients.map(recipient => ({
        ...recipient,
        label: `#${recipient.userId} · ${recipient.displayName} · ${recipient.email || '-'} · ${recipient.mobile || 'Telefon yok'}`,
        disabled: !recipient.whatsappPhone,
      }))
    },
    selectedRecipient() {
      const selectedId = String(this.selectedRecipientId ?? '')
      const currentRecipient = this.recipientOptions.find(
        recipient => String(recipient.userId) === selectedId,
      )

      if (currentRecipient) return currentRecipient

      if (String(this.selectedRecipientSnapshot?.userId ?? '') === selectedId) {
        return this.selectedRecipientSnapshot
      }

      return null
    },
    canSendTemplate() {
      return Boolean(
        this.selectedConversation
        && this.selectedSupportTemplate?.approved
        && !this.sendingTemplate,
      )
    },
    templateStatusLabel() {
      const status = String(this.selectedSupportTemplate?.providerStatus || '').toUpperCase()
      const labels = {
        APPROVED: 'Onaylı',
        PENDING: 'Meta onayı bekleniyor',
        REJECTED: 'Reddedildi',
        PAUSED: 'Durduruldu',
        DISABLED: 'Devre dışı',
        NOT_FOUND: 'Meta’da bulunamadı',
        NOT_CONFIGURED: 'Yapılandırılmadı',
        UNAVAILABLE: 'Durum alınamadı',
      }

      return labels[status] || status || 'Bilinmiyor'
    },
    templateStatusColor() {
      return this.selectedSupportTemplate?.approved ? 'success' : 'warning'
    },
    templateSendBlockedReason() {
      if (!this.selectedSupportTemplate || this.selectedSupportTemplate.approved) return ''

      return `${this.templateStatusLabel}. Meta template'i onayladıktan sonra gönderim otomatik olarak açılır.`
    },
    canReply() {
      return this.selectedConversation?.canReply !== false
    },
    replyBlockedReason() {
      if (this.canReply) return ''

      return this.selectedConversation?.replyBlockedReason
        || 'Kullanıcının son WhatsApp mesajından sonra 24 saat geçti. Serbest metin cevap WhatsApp tarafından iletilmez; kullanıcı tekrar mesaj yazana kadar cevap gönderilemez.'
    },
  },
  mounted() {
    this.loadConversations()
    this.loadTemplates()
  },
  beforeDestroy() {
    this.conversationSequence += 1
    clearTimeout(this.recipientLookupTimeout)
    this.revokeMediaObjectUrls()
  },
  beforeRouteLeave(to, from, next) { next(!this.reviewDirty && !this.reviewBusy && !this.sending && !this.sendingTemplate) },
  methods: {
    priorityLabel(value) {
      return {
        LOW: 'Düşük', NORMAL: 'Normal', HIGH: 'Yüksek', URGENT: 'Acil',
      }[value] || 'Normal'
    },
    reviewSaved(row) { this.$set(this.reviews, row.recordId, row) },
    showNewConversationDialog() {
      if (this.reviewDirty || this.reviewBusy) return
      this.newConversationDialog = true
      this.recipientSearch = ''
      this.recipients = []
      this.selectedRecipientId = null
      this.selectedRecipientSnapshot = null
      this.recipientError = ''
    },
    closeNewConversationDialog() {
      if (this.openingConversation) return
      clearTimeout(this.recipientLookupTimeout)
      this.newConversationDialog = false
      this.recipientSearch = ''
      this.recipients = []
      this.selectedRecipientId = null
      this.selectedRecipientSnapshot = null
      this.recipientError = ''
    },
    rememberSelectedRecipient(value) {
      const selectedId = String(value ?? '')
      if (!selectedId) {
        this.selectedRecipientSnapshot = null

        return
      }

      const recipient = this.recipientOptions.find(
        option => String(option.userId) === selectedId,
      )
      if (recipient) {
        this.selectedRecipientSnapshot = { ...recipient }
      }
    },
    queueRecipientSearch(value) {
      clearTimeout(this.recipientLookupTimeout)
      this.recipientError = ''

      const query = String(value || '').trim()
      if (!query) {
        if (!this.selectedRecipientId) {
          this.recipients = []
          this.selectedRecipientSnapshot = null
        }

        return
      }

      if (query.length < 2 && !/^\d+$/.test(query)) {
        if (!this.selectedRecipientId) this.recipients = []

        return
      }

      this.recipientLookupTimeout = setTimeout(() => {
        this.searchRecipients(query)
      }, 300)
    },
    async searchRecipients(query) {
      this.searchingRecipients = true
      try {
        const response = await axios.get(`${this.apiBase}/recipients`, {
          ...this.requestOptions,
          params: {
            query,
            limit: 20,
          },
        })
        this.recipients = Array.isArray(response.data?.data) ? response.data.data : []
      } catch (error) {
        this.recipients = []
        this.recipientError = this.resolveError(error, 'Kullanıcı araması yapılamadı')
      } finally {
        this.searchingRecipients = false
      }
    },
    async openRecipientConversation() {
      if (!this.selectedRecipient || this.selectedRecipient.disabled || this.openingConversation) return

      const recipient = this.selectedRecipient
      this.openingConversation = true
      this.recipientError = ''
      try {
        const response = await axios.post(
          `${this.apiBase}/conversations`,
          { userId: Number(recipient.userId) },
          this.requestOptions,
        )
        const responseConversation = response.data?.data
        const conversation = responseConversation
          ? {
            ...responseConversation,
            userId: responseConversation.userId ?? recipient.userId,
            phoneVerified: responseConversation.phoneVerified ?? recipient.phoneVerified,
          }
          : null
        if (!conversation?.id) {
          throw new Error('CONVERSATION_NOT_CREATED')
        }

        this.newConversationDialog = false
        const existingIndex = this.conversations.findIndex(item => item.id === conversation.id)
        if (existingIndex >= 0) {
          this.$set(this.conversations, existingIndex, conversation)
        } else {
          this.conversations.unshift(conversation)
        }
        await this.selectConversation(conversation)
        this.templateMenu = true
      } catch (error) {
        this.recipientError = this.resolveError(error, 'WhatsApp konuşması açılamadı')
      } finally {
        this.openingConversation = false
      }
    },
    async refreshInbox() {
      await Promise.all([this.loadConversations(), this.loadTemplates()])
      await this.refreshSelectedConversation()
      this.scrollMessagesToBottom()
    },
    async loadTemplates() {
      this.loadingTemplates = true
      this.templateError = ''
      try {
        const response = await axios.get(`${this.apiBase}/templates`, this.requestOptions)
        this.supportTemplates = Array.isArray(response.data?.data) ? response.data.data : []
        if (!this.supportTemplates.some(template => template.name === this.selectedTemplateName)) {
          this.selectedTemplateName = this.supportTemplates[0]?.name || ''
        }
      } catch (error) {
        this.supportTemplates = []
        this.selectedTemplateName = ''
        this.templateError = this.resolveError(error, 'WhatsApp template bilgileri alınamadı')
      } finally {
        this.loadingTemplates = false
      }
    },
    async loadConversations() {
      this.loadingConversations = true
      this.error = ''
      try {
        const response = await axios.get(`${this.apiBase}/conversations`, this.requestOptions)
        this.conversations = Array.isArray(response.data?.data) ? response.data.data : []
        this.reviewLoadError = ''
        if (this.conversations.length) {
          try {
            const reviewResponse = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/support-reviews/WHATSAPP`, { ...this.requestOptions, params: { ids: this.conversations.map(row => row.id).join(',') } })
            this.reviews = Object.fromEntries(reviewResponse.data.map(row => [row.recordId, row]))
          } catch (reviewError) { this.reviewLoadError = 'İç inceleme bilgileri yüklenemedi.'; this.inboxAssignee = '' }
        }
        if (this.selectedConversation) {
          const selected = this.conversations.find(item => item.id === this.selectedConversation.id)
          this.selectedConversation = selected || this.selectedConversation
        }
      } catch (error) {
        this.error = this.resolveError(error, 'Konuşmalar alınamadı')
      } finally {
        this.loadingConversations = false
      }
    },
    async selectConversation(conversation) {
      if (this.reviewDirty || this.reviewBusy || this.sending || this.sendingTemplate) return
      this.conversationSequence += 1
      const sequence = this.conversationSequence
      this.revokeMediaObjectUrls()
      this.selectedConversation = conversation
      this.messages = []
      this.reply = ''
      this.replyTarget = null
      this.serverHiddenOutboundMessageCount = 0
      this.hasMoreMessages = false
      this.nextBeforeMessageId = null
      this.loadingMessages = true
      this.error = ''
      try {
        const response = await axios.get(`${this.apiBase}/conversations/${conversation.id}`, this.requestOptions)
        if (sequence !== this.conversationSequence) return
        const payload = response.data?.data || {}
        this.selectedConversation = payload.conversation || conversation
        this.messages = Array.isArray(payload.messages) ? payload.messages : []
        this.serverHiddenOutboundMessageCount = Number(payload.hiddenOutboundMessageCount || 0)
        this.hasMoreMessages = Boolean(payload.hasMoreMessages)
        this.nextBeforeMessageId = payload.nextBeforeMessageId ?? null
        this.replaceConversation(this.selectedConversation)
        this.loadMediaAssets()
        this.scrollMessagesToBottom()
      } catch (error) {
        if (sequence === this.conversationSequence) this.error = this.resolveError(error, 'Mesajlar alınamadı')
      } finally {
        if (sequence === this.conversationSequence) this.loadingMessages = false
      }
    },
    async loadOlderMessages() {
      if (
        !this.selectedConversation?.id
        || !this.nextBeforeMessageId
        || this.loadingOlderMessages
      ) return

      const conversationId = this.selectedConversation.id
      const pane = this.$refs.messagePane
      const previousScrollHeight = pane?.scrollHeight || 0
      this.loadingOlderMessages = true
      this.error = ''
      try {
        const response = await axios.get(
          `${this.apiBase}/conversations/${conversationId}/messages`,
          {
            ...this.requestOptions,
            params: {
              beforeMessageId: this.nextBeforeMessageId,
              limit: 100,
            },
          },
        )
        if (this.selectedConversation?.id !== conversationId) return

        const payload = response.data?.data || {}
        const olderMessages = Array.isArray(payload.messages) ? payload.messages : []
        const existingIds = new Set(this.messages.map(message => String(message.id)))
        this.messages = [
          ...olderMessages.filter(message => !existingIds.has(String(message.id))),
          ...this.messages,
        ]
        this.serverHiddenOutboundMessageCount += Number(payload.hiddenOutboundMessageCount || 0)
        this.hasMoreMessages = Boolean(payload.hasMoreMessages)
        this.nextBeforeMessageId = payload.nextBeforeMessageId ?? null
        this.$nextTick(() => {
          const currentPane = this.$refs.messagePane
          if (currentPane) {
            currentPane.scrollTop += currentPane.scrollHeight - previousScrollHeight
          }
        })
      } catch (error) {
        this.error = this.resolveError(error, 'Eski mesajlar alınamadı')
      } finally {
        this.loadingOlderMessages = false
      }
    },
    insertEmoji(emoji) {
      if (!this.canReply) return

      const input = this.$refs.replyInput?.$refs?.input
      if (!input) {
        this.reply += emoji
        this.emojiMenu = false

        return
      }

      const start = Number.isInteger(input.selectionStart) ? input.selectionStart : this.reply.length
      const end = Number.isInteger(input.selectionEnd) ? input.selectionEnd : start
      this.reply = `${this.reply.slice(0, start)}${emoji}${this.reply.slice(end)}`
      this.emojiMenu = false
      this.$nextTick(() => {
        input.focus()
        const cursor = start + emoji.length
        input.setSelectionRange(cursor, cursor)
      })
    },
    isReactionMessage(message) {
      return String(message?.messageType || '').toLowerCase() === 'reaction'
    },
    hasReactionTarget(reaction) {
      if (!reaction?.reactionToMetaMessageId) return false

      return this.messages.some(message => message.metaMessageId === reaction.reactionToMetaMessageId)
    },
    isMediaMessage(message) {
      return Boolean(message?.mediaAvailable)
        && ['image', 'video', 'audio', 'document', 'sticker'].includes(String(message?.messageType || '').toLowerCase())
    },
    isKnownMediaMessage(message) {
      return ['image', 'video', 'audio', 'document', 'sticker'].includes(String(message?.messageType || '').toLowerCase())
    },
    mediaKind(message) {
      return String(message?.messageType || '').toLowerCase()
    },
    isImageMedia(message) {
      return ['image', 'sticker'].includes(this.mediaKind(message))
    },
    isVideoMedia(message) {
      return this.mediaKind(message) === 'video'
    },
    isAudioMedia(message) {
      return this.mediaKind(message) === 'audio'
    },
    mediaCaption(message) {
      const body = String(message?.body || '').trim()

      return /^\[[a-z]+\]$/i.test(body) ? '' : body
    },
    mediaUnavailableLabel(message) {
      const labels = {
        image: 'Görsel WhatsApp’tan alınamadı.',
        video: 'Video WhatsApp’tan alınamadı.',
        audio: 'Ses kaydı WhatsApp’tan alınamadı.',
        document: 'Dosya WhatsApp’tan alınamadı.',
        sticker: 'Sticker WhatsApp’tan alınamadı.',
      }

      return labels[this.mediaKind(message)] || 'Medya WhatsApp’tan alınamadı.'
    },
    mediaLoadLabel(message) {
      const labels = {
        image: 'Görseli görüntüle',
        video: 'Videoyu görüntüle',
        audio: 'Ses kaydını yükle',
        document: 'Dosyayı yükle',
        sticker: 'Sticker’ı görüntüle',
      }

      return labels[this.mediaKind(message)] || 'Medyayı yükle'
    },
    async loadMediaAssets() {
      this.scrollMessagesToBottom()
    },
    async loadMessageMedia(message) {
      if (!message?.id || this.mediaLoading[message.id] || this.mediaObjectUrls[message.id]) return

      this.$set(this.mediaLoading, message.id, true)
      this.$delete(this.mediaErrors, message.id)
      try {
        const response = await axios.get(`${this.apiBase}/messages/${message.id}/media`, {
          ...this.requestOptions,
          responseType: 'blob',
        })
        this.$set(this.mediaObjectUrls, message.id, URL.createObjectURL(response.data))
      } catch (error) {
        this.$set(this.mediaErrors, message.id, this.resolveError(error, 'Medya yüklenemedi'))
      } finally {
        this.$delete(this.mediaLoading, message.id)
      }
    },
    openMedia(message) {
      const url = this.mediaObjectUrls[message?.id]
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    },
    revokeMediaObjectUrls() {
      Object.values(this.mediaObjectUrls).forEach(url => URL.revokeObjectURL(url))
      this.mediaObjectUrls = {}
      this.mediaLoading = {}
      this.mediaErrors = {}
    },
    canReactTo(message) {
      if (!message?.metaMessageId || message.direction !== 'INBOUND' || this.isReactionMessage(message)) return false

      const timestamp = new Date(message.messageTimestamp)
      if (Number.isNaN(timestamp.getTime())) return false

      return Date.now() - timestamp.getTime() < 30 * 24 * 60 * 60 * 1000
    },
    canReplyToMessage(message) {
      const direction = String(message?.direction || '').toUpperCase()

      return Boolean(
        this.canReply
        && message?.metaMessageId
        && ['INBOUND', 'OUTBOUND'].includes(direction)
        && !this.isReactionMessage(message),
      )
    },
    selectReplyTarget(message) {
      if (!this.canReplyToMessage(message)) return
      this.replyTarget = message
      this.$nextTick(() => this.$refs.replyInput?.focus())
    },
    clearReplyTarget() {
      this.replyTarget = null
    },
    replyTargetFor(message) {
      if (!message?.replyToMetaMessageId) return null

      return this.messages.find(item => item.metaMessageId === message.replyToMetaMessageId) || null
    },
    replySenderLabel(message) {
      if (!message) return 'Yanıtlanan mesaj'

      return message.direction === 'OUTBOUND' ? 'Adverport Destek' : this.contactTitle(this.selectedConversation)
    },
    replyPreview(message) {
      if (!message) return 'Mesaj artık konuşma geçmişinde bulunmuyor.'

      const type = this.mediaKind(message)
      const mediaLabels = {
        image: 'Görsel',
        video: 'Video',
        audio: 'Ses kaydı',
        document: message.mediaFileName || 'Dosya',
        sticker: 'Sticker',
      }
      const body = String(message.body || '').trim()
      const content = /^\[[a-z]+\]$/i.test(body)
        ? mediaLabels[type] || 'Medya'
        : body || mediaLabels[type] || 'Mesaj'

      return content.length > 100 ? `${content.slice(0, 100)}…` : content
    },
    messageReactions(message) {
      if (!message?.metaMessageId) return []

      const latestByDirection = {}
      this.messages.forEach(reaction => {
        if (!this.isReactionMessage(reaction) || reaction.reactionToMetaMessageId !== message.metaMessageId) return
        latestByDirection[reaction.direction] = reaction
      })

      return Object.values(latestByDirection).filter(
        reaction => !reaction.reactionRemoved && reaction.reactionEmoji,
      )
    },
    currentOutboundReaction(message) {
      return this.messageReactions(message).find(reaction => reaction.direction === 'OUTBOUND') || null
    },
    isSendingReaction(message) {
      return Boolean(this.sendingReactions[message?.metaMessageId])
    },
    async sendReaction(message, selectedEmoji) {
      if (!this.canReactTo(message) || this.isSendingReaction(message)) return

      const currentReaction = this.currentOutboundReaction(message)
      const emoji = currentReaction?.reactionEmoji === selectedEmoji ? '' : selectedEmoji
      this.$set(this.sendingReactions, message.metaMessageId, true)
      this.error = ''
      try {
        const response = await axios.post(
          `${this.apiBase}/conversations/${this.selectedConversation.id}/reaction`,
          {
            targetMessageId: message.metaMessageId,
            emoji,
          },
          this.requestOptions,
        )
        if (response.data?.data) {
          this.messages.push(response.data.data)
        }
      } catch (error) {
        this.error = this.resolveError(error, 'Reaksiyon gönderilemedi')
        await this.refreshSelectedConversation()
      } finally {
        this.$delete(this.sendingReactions, message.metaMessageId)
      }
    },
    async sendReply() {
      const message = this.reply.trim()
      if (!message || !this.selectedConversation || this.sending || !this.canReply) return

      this.sending = true
      this.error = ''
      try {
        const response = await axios.post(
          `${this.apiBase}/conversations/${this.selectedConversation.id}/reply`,
          {
            message,
            replyToMessageId: this.replyTarget?.metaMessageId || null,
          },
          this.requestOptions,
        )
        if (response.data?.data) {
          this.messages.push(response.data.data)
        }
        this.reply = ''
        this.replyTarget = null
        await this.loadConversations()
        this.scrollMessagesToBottom()
      } catch (error) {
        this.error = this.resolveError(error, 'Cevap gönderilemedi')
        await this.refreshSelectedConversation()
      } finally {
        this.sending = false
      }
    },
    async sendSupportTemplate() {
      if (!this.canSendTemplate) return

      this.sendingTemplate = true
      this.templateError = ''
      this.error = ''
      try {
        const response = await axios.post(
          `${this.apiBase}/conversations/${this.selectedConversation.id}/template`,
          {
            templateName: this.selectedSupportTemplate.name,
            language: this.selectedSupportTemplate.language,
          },
          this.requestOptions,
        )
        if (response.data?.data) {
          this.messages.push(response.data.data)
        }
        await this.loadConversations()
        this.templateMenu = false
        this.scrollMessagesToBottom()
      } catch (error) {
        const sendError = this.resolveError(error, 'Template mesajı gönderilemedi')
        await Promise.all([this.loadTemplates(), this.refreshSelectedConversation()])
        this.templateError = sendError
      } finally {
        this.sendingTemplate = false
      }
    },
    async refreshSelectedConversation() {
      if (!this.selectedConversation?.id) return
      try {
        const response = await axios.get(`${this.apiBase}/conversations/${this.selectedConversation.id}`, this.requestOptions)
        const payload = response.data?.data || {}
        this.selectedConversation = payload.conversation || this.selectedConversation
        this.revokeMediaObjectUrls()
        this.messages = Array.isArray(payload.messages) ? payload.messages : []
        this.serverHiddenOutboundMessageCount = Number(payload.hiddenOutboundMessageCount || 0)
        this.hasMoreMessages = Boolean(payload.hasMoreMessages)
        this.nextBeforeMessageId = payload.nextBeforeMessageId ?? null
        this.replaceConversation(this.selectedConversation)
        this.loadMediaAssets()
      } catch (ignored) {
        // Keep the original send error visible.
      }
    },
    replaceConversation(conversation) {
      const index = this.conversations.findIndex(item => item.id === conversation.id)
      if (index >= 0) {
        this.$set(this.conversations, index, conversation)
      }
    },
    scrollMessagesToBottom() {
      this.$nextTick(() => {
        const pane = this.$refs.messagePane
        if (pane) {
          pane.scrollTop = pane.scrollHeight
        }
      })
    },
    contactTitle(conversation) {
      return conversation?.contactName || conversation?.contactWaId || '-'
    },
    initials(conversation) {
      const value = this.contactTitle(conversation)

      return value
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'WA'
    },
    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    isSystemMessage(message) {
      return message?.direction === 'SYSTEM'
    },
    shouldHideMessage(message) {
      if (!message || message.direction !== 'OUTBOUND') return false

      const status = String(message.messageStatus || '').trim().toLowerCase()

      return status === 'failed' || status === 'undelivered'
    },
    resolveError(error, fallback) {
      return error?.response?.data?.message || error?.response?.data?.data || fallback
    },
  },
}
</script>

<style lang="scss" scoped>
.support-panel {
  border-radius: 8px;
  min-height: 620px;
}

.support-panel__title {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.support-panel__actions,
.support-thread__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.support-panel__actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.support-thread__actions {
  flex: 0 0 auto;
}

.support-conversations {
  max-height: 548px;
  overflow-y: auto;
}

.support-conversation--active {
  background: rgba(210, 16, 39, 0.08);
}

.support-conversation__meta {
  align-items: flex-end;
  min-width: 72px;
}

.support-thread {
  display: flex;
  flex-direction: column;
}

.support-messages {
  height: 454px;
  overflow-y: auto;
  padding: 20px;
  background: var(--v-background-base, #f7f7f9);
}

.support-history-loader {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.support-message {
  display: flex;
  margin-bottom: 12px;
}

.support-message--outbound {
  justify-content: flex-end;
}

.support-message--system {
  justify-content: center;
}

.support-message__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: min(520px, 82%);
}

.support-message--outbound .support-message__content {
  align-items: flex-end;
}

.support-message--system .support-message__content {
  align-items: center;
  max-width: min(460px, 90%);
}

.support-message__bubble {
  max-width: 100%;
  border-radius: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid rgba(94, 86, 105, 0.16);
}

.support-message--outbound .support-message__bubble {
  background: #d21027;
  color: #ffffff;
  border-color: #d21027;
}

.support-message--system .support-message__bubble {
  background: #eef0f3;
  color: rgba(94, 86, 105, 0.86);
  border-color: rgba(94, 86, 105, 0.12);
  text-align: center;
  padding: 8px 12px;
}

.support-message__body {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.45;
}

.support-message__quote {
  display: grid;
  gap: 2px;
  max-width: 100%;
  margin-bottom: 8px;
  padding: 7px 9px;
  border-left: 3px solid #d21027;
  border-radius: 4px;
  background: rgba(210, 16, 39, 0.07);
  font-size: 12px;
  line-height: 1.35;
}

.support-message__quote span {
  overflow: hidden;
  opacity: 0.76;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-message--outbound .support-message__quote {
  border-left-color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.14);
}

.support-message__media {
  min-width: 210px;
}

.support-message__image,
.support-message__video {
  display: block;
  width: 100%;
  max-width: 420px;
  max-height: 320px;
  border-radius: 6px;
  object-fit: contain;
  background: rgba(0, 0, 0, 0.04);
}

.support-message__image {
  cursor: zoom-in;
}

.support-message__audio {
  display: block;
  width: min(340px, 100%);
}

.support-message__document,
.support-message__media-error,
.support-message__media-state {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
}

.support-message__document {
  color: inherit;
  font-weight: 600;
  text-decoration: none;
}

.support-message__media-error {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.support-message__media-state--unavailable {
  color: inherit;
  opacity: 0.72;
}

.support-message__caption {
  margin-top: 8px;
}

.support-message__reaction-note {
  font-size: 11px;
  opacity: 0.7;
}

.support-message--system .support-message__body {
  font-size: 12px;
  font-weight: 600;
}

.support-message__meta {
  margin-top: 6px;
  font-size: 11px;
  opacity: 0.72;
  text-align: right;
}

.support-message--system .support-message__meta {
  text-align: center;
}

.support-message__reactions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  padding: 0 8px;
  position: relative;
  z-index: 1;
}

.support-message__reaction {
  min-width: 32px;
  height: 24px;
  padding: 1px 7px;
  border: 1px solid rgba(210, 16, 39, 0.28);
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(18, 24, 40, 0.08);
  font-size: 15px;
  line-height: 20px;
  cursor: default;
}

.support-message__reaction--ours {
  cursor: pointer;
}

.support-message__actions {
  display: flex;
  gap: 2px;
  margin-top: 2px;
}

.support-message__action {
  margin-top: 2px;
  opacity: 0.48;
}

.support-message__action:hover {
  opacity: 1;
}

.support-reply {
  display: flex;
  align-items: flex-end;
  padding: 16px;
}

.support-reply__body {
  flex: 1 1 auto;
}

.support-reply-target {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  padding: 8px 10px;
  border-left: 3px solid #d21027;
  border-radius: 6px;
  background: rgba(210, 16, 39, 0.06);
}

.support-reply-target__content {
  display: grid;
  gap: 2px;
  min-width: 0;
  font-size: 12px;
}

.support-reply-target__content span {
  overflow: hidden;
  color: rgba(94, 86, 105, 0.74);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-reply__editor {
  position: relative;
}

.support-reply__editor ::v-deep textarea {
  padding-right: 40px;
}

.support-reply__emoji-button {
  position: absolute;
  right: 7px;
  bottom: 7px;
}

.support-emoji-picker {
  display: grid;
  grid-template-columns: repeat(8, 38px);
  gap: 4px;
  padding: 10px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(24, 22, 28, 0.16);
}

.support-emoji-picker--reaction {
  grid-template-columns: repeat(8, 36px);
  padding: 8px;
}

.support-emoji-picker--message {
  display: block;
  width: 360px;
  max-width: calc(100vw - 32px);
  padding: 8px;
}

.support-emoji-picker__categories {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 3px;
  padding-bottom: 7px;
  border-bottom: 1px solid rgba(94, 86, 105, 0.12);
}

.support-emoji-picker__category {
  height: 34px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
}

.support-emoji-picker__category:hover,
.support-emoji-picker__category--active {
  background: rgba(210, 16, 39, 0.1);
}

.support-emoji-picker__grid {
  display: grid;
  grid-template-columns: repeat(8, 38px);
  gap: 4px;
  max-height: 250px;
  margin-top: 7px;
  overflow-y: auto;
}

.support-emoji-picker__item {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 22px;
  line-height: 38px;
  text-align: center;
  cursor: pointer;
}

.support-emoji-picker__item:hover,
.support-emoji-picker__item:focus-visible {
  background: rgba(210, 16, 39, 0.08);
  outline: none;
}

.support-template__preview {
  margin: 10px 0;
  padding: 10px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid rgba(94, 86, 105, 0.12);
  font-size: 12px;
  line-height: 1.45;
}

.support-template-menu {
  border-top: 3px solid #25d366;
}

.support-empty {
  min-height: 620px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(94, 86, 105, 0.72);
}

@media (max-width: 1263px) {
  .support-panel {
    min-height: auto;
  }

  .support-messages {
    height: 420px;
  }

  .support-thread__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
